import { type DateRange } from "@mui/x-date-pickers-pro";
import { type DateTime } from "luxon";

import { packNetAxios } from "@/lib/api-client";
import type { PlannedProductionData } from "@/schemas";
import { formatUri } from "@/utils/datetime";

export interface ApiRequestBody {
	readonly schedules: PlannedProductionData[];
	readonly sites: string[];
	readonly machines?: string[];
}

const END_OF_DAY_HOUR = 23;
const END_OF_DAY_MINUTE = 59;
const END_OF_DAY_SECOND = 59;
const INCLUSIVE_END_OF_DAY_ADJUSTMENT_SECONDS = 1;

export const constructUrl = (
	endpoint: string,
	dateRange: DateRange<DateTime<boolean>>,
	timeZoneId: string,
	options?: { readonly excludeBuiltInWasteInTrimCalculation?: boolean },
): string => {
	const endDate = dateRange[1];
	const isEndOfDay =
		endDate &&
		endDate.hour === END_OF_DAY_HOUR &&
		endDate.minute === END_OF_DAY_MINUTE &&
		endDate.second === END_OF_DAY_SECOND;
	const adjustedEndDate = isEndOfDay ? endDate.plus({ seconds: INCLUSIVE_END_OF_DAY_ADJUSTMENT_SECONDS }) : endDate;

	const params = new URLSearchParams({
		startDate: formatUri(dateRange[0]),
		endDate: formatUri(adjustedEndDate),
		timeZoneId,
	});

	if (options?.excludeBuiltInWasteInTrimCalculation !== undefined) {
		params.append("excludeBuiltInWasteInTrimCalculation", options.excludeBuiltInWasteInTrimCalculation.toString());
	}

	return `${endpoint}?${params.toString()}`;
};

export const constructUrlFromDateTime = (
	endpoint: string,
	startDate: DateTime,
	endDate: DateTime,
	timeZoneId: string,
	options?: { readonly excludeBuiltInWasteInTrimCalculation?: boolean },
): string => {
	const isEndOfDay =
		endDate.hour === END_OF_DAY_HOUR && endDate.minute === END_OF_DAY_MINUTE && endDate.second === END_OF_DAY_SECOND;
	const adjustedEndDate = isEndOfDay ? endDate.plus({ seconds: INCLUSIVE_END_OF_DAY_ADJUSTMENT_SECONDS }) : endDate;

	const params = new URLSearchParams({
		startDate: formatUri(startDate),
		endDate: formatUri(adjustedEndDate),
		timeZoneId,
	});

	if (options?.excludeBuiltInWasteInTrimCalculation !== undefined) {
		params.append("excludeBuiltInWasteInTrimCalculation", options.excludeBuiltInWasteInTrimCalculation.toString());
	}

	return `${endpoint}?${params.toString()}`;
};

export const fetchData = async <T>(url: string, body: ApiRequestBody, signal?: AbortSignal): Promise<T | undefined> => {
	try {
		const response = await packNetAxios.post<T>(url, body, { signal });
		return response?.data;
	} catch (error: unknown) {
		if (typeof error === "object" && error !== null && (error as { name?: string }).name === "CanceledError") {
			return Promise.resolve(undefined);
		}

		console.error("API request failed:", url, error);
		throw error;
	}
};
